import React from 'react';
import PropTypes from 'prop-types';
import PictionJS from 'piction-js';
import cn from 'classnames';
import { Dropdown, DropdownButton } from 'react-bootstrap';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { downloadURL } from '../../utils/umoUtils';
import {
  showCropTool,
  showImageAnnotatorTool,
  showImageResizeTool,
  showVideoAnnotatorTool,
  showVideoTrimmerTool,
  showMetaEditTool,
} from '../../utils/uiUtils';
import { isEditMode } from '../../utils/piction';
import { UMO_TYPE_IMAGE, UMO_TYPE_VIDEO } from '../../constants/umo';
import appConfig from '../../appConfig';

const KEY_DOWNLOAD = 'KEY_DOWNLOAD';
const KEY_CROP = 'KEY_CROP';
const KEY_IMAGE_ANNOTATE = 'KEY_IMAGE_ANNOTATE';
const KEY_RESIZE = 'KEY_RESIZE';
const KEY_ANNOTATE = 'KEY_ANNOTATE';
const KEY_TRIMMER = 'KEY_TRIMMER';
const KEY_META_EDIT = 'KEY_META_EDIT';
const KEY_VIEW_RELATED = 'KEY_VIEW_RELATED';

const mapDispatchToProps = dispatch => ({
  dispatch: actionToDispatch => dispatch(actionToDispatch),
});

function UMOGridItemOverlayMenu({
  umo, retrieveRelatedUMOs, intl, dispatch, classModifier, isRestricted,
}) {
  const itemClass = 'umo-grid-item__overlay-more-images-option';

  const onMenuItemClick = (e) => {
    const type = e.target.dataset.keytype;
    const data = e.target.dataset.keyval;
    switch (type) {
      case KEY_DOWNLOAD:
        downloadURL(data, umo.name, dispatch);
        break;
      case KEY_CROP:
        showCropTool(umo, dispatch);
        break;
      case KEY_IMAGE_ANNOTATE:
        showImageAnnotatorTool(umo, dispatch);
        break;
      case KEY_RESIZE:
        showImageResizeTool(umo, intl.locale, dispatch);
        break;
      case KEY_ANNOTATE:
        showVideoAnnotatorTool(umo, dispatch);
        break;
      case KEY_TRIMMER:
        showVideoTrimmerTool(umo, dispatch);
        break;
      case KEY_META_EDIT:
        showMetaEditTool(umo, dispatch);
        break;
      case KEY_VIEW_RELATED:
        retrieveRelatedUMOs(umo.id);
        break;
      default:
        break;
    }
    e.stopPropagation();
  };

  const toolOptions = [];
  let showRelateMenu = false;
  let relateOption = '';
  const isUMOMenu = appConfig.menuOptions &&
    appConfig.menuOptions.umoMenu;

  if (isUMOMenu && appConfig.menuOptions.umoMenu.length > 0) {
    const menuOptions = appConfig.menuOptions.umoMenu;
    menuOptions.forEach((value) => {
      switch (value) {
        case 'editMetadata':
          toolOptions.push({ key: KEY_META_EDIT, label: intl.formatMessage({ id: 'umoGridItem.menuEditMetaData' }) });
          break;
        case 'crop':
          if (umo.type === UMO_TYPE_IMAGE && isEditMode() && !isRestricted) {
            toolOptions.push({ key: KEY_CROP, label: intl.formatMessage({ id: 'umoGridItem.menuCrop' }) });
          }
          break;
        case 'imageAnno':
          if (umo.type === UMO_TYPE_IMAGE && isEditMode() && !isRestricted) {
            toolOptions.push({ key: KEY_IMAGE_ANNOTATE, label: intl.formatMessage({ id: 'umoGridItem.menuImageAnnotator' }) });
          }
          break;
        case 'resize':
          if (umo.type === UMO_TYPE_IMAGE && isEditMode() && !isRestricted) {
            toolOptions.push({ key: KEY_RESIZE, label: intl.formatMessage({ id: 'umoGridItem.menuResize' }) });
          }
          break;
        case 'videoAnno':
          if (umo.type === UMO_TYPE_VIDEO && isEditMode() && !isRestricted) {
            toolOptions.push({ key: KEY_ANNOTATE, label: intl.formatMessage({ id: 'umoGridItem.menuVideoAnnotator' }) });
          }
          break;
        case 'videoSnip':
          if (umo.type === UMO_TYPE_VIDEO && !isRestricted) {
            toolOptions.push({ key: KEY_TRIMMER, label: intl.formatMessage({ id: 'umoGridItem.menuVideoSnippet' }) });
          }
          break;
        case 'relateUMOs':
          showRelateMenu = true;
          relateOption = (
            <Dropdown.Item
              key={menuOptions.length + 1}
              className={itemClass}
              eventKey={KEY_VIEW_RELATED}
              onClick={onMenuItemClick}
              data-keytype={KEY_VIEW_RELATED}
            >
              {intl.formatMessage({ id: 'umoGridItem.menuViewPeers' })}
            </Dropdown.Item>);
          break;
        default:
          break;
      }
    });
  } else if (isEditMode() && !isUMOMenu) {
    toolOptions.push({ key: KEY_META_EDIT, label: intl.formatMessage({ id: 'umoGridItem.menuEditMeta' }) });

    switch (umo.type) {
      case UMO_TYPE_IMAGE:
        if (!isRestricted) {
          toolOptions.push({ key: KEY_CROP, label: intl.formatMessage({ id: 'umoGridItem.menuCrop' }) });
          toolOptions.push({ key: KEY_RESIZE, label: intl.formatMessage({ id: 'umoGridItem.menuResize' }) });
        }
        break;
      case UMO_TYPE_VIDEO:
        if (!isRestricted) {
          toolOptions.push({ key: KEY_ANNOTATE, label: intl.formatMessage({ id: 'umoGridItem.menuVideoAnnotator' }) });
          toolOptions.push({ key: KEY_TRIMMER, label: intl.formatMessage({ id: 'umoGridItem.menuVideoSnippet' }) });
        }
        break;
      default:
        break;
    }
  }

  let downloads;
  // TODO: add restricted per individual download- disable
  if (umo.originalList.all.length > 0 && !isRestricted) {
    downloads = umo.originalList.all.map((downloadData, i) =>
      <Dropdown.Item
        className={itemClass}
        key={i}
        eventKey={KEY_DOWNLOAD}
        data-keytype={KEY_DOWNLOAD}
        data-keyval={downloadData.url}
        onClick={onMenuItemClick}
      >
        {downloadData.name}
      </Dropdown.Item>
    );
  } else {
    downloads = (
      <Dropdown.Item className={itemClass} disabled>
        {intl.formatMessage({ id: 'umoGridItem.menuNoDownloadsAvailableMessage' })}
      </Dropdown.Item>);
  }

  return (
    <DropdownButton
      title={
        <span className={cn('umo-grid-item__overlay-more-images-icon', classModifier)}><i className="fas fa-ellipsis-h" /></span>
      }
      variant="default"
      id="umo-grid-item__overlay-download"
      className="umo-grid-item__overlay-more-images-btn"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      alignRight
    >
      <Dropdown.Header>{intl.formatMessage({ id: 'umoGridItem.menuDownloadsHeader' })}</Dropdown.Header>
      {downloads}
      {showRelateMenu && <Dropdown.Header>{intl.formatMessage({ id: 'umoGridItem.menuRelatedImagesHeader' })}</Dropdown.Header>}
      {relateOption}
      {toolOptions.length > 0 && <Dropdown.Header>{intl.formatMessage({ id: 'umoGridItem.toolsHeader' })}</Dropdown.Header>}
      {toolOptions.map(data => (
        <Dropdown.Item
          key={data.key}
          className={itemClass}
          eventKey={data.key}
          data-keytype={data.key}
          onClick={onMenuItemClick}
        >
          {data.label}
        </Dropdown.Item>
          ))
        }
    </DropdownButton>
  );
}
UMOGridItemOverlayMenu.propTypes = {
  umo: PropTypes.instanceOf(PictionJS.UMO),
  retrieveRelatedUMOs: PropTypes.func,
  dispatch: PropTypes.func,
  classModifier: PropTypes.string,
  isRestricted: PropTypes.bool,
};

export default injectIntl(connect(null, mapDispatchToProps)(UMOGridItemOverlayMenu));



// WEBPACK FOOTER //
// ./src/js/components/UMOGridItem/UMOGridItemOverlayMenu.jsx